import {
  isNone,
  Maybe,
  parsePhoneNumber,
  VALID_PHONE_FORMATS,
} from '@crocodille/util';
import { t } from 'i18next';
import { useMemo } from 'react';
import { FormFieldInit, useFormField, useValidator } from './forms';

export const PHONE_NUMBER_FORMAT_VALIDATOR = (value: string) => {
  if (/^[0-9]*$/.test(value.replace(/\s+/g, ''))) {
    return null;
  } else {
    return t('validation_error.phone_number_format');
  }
};

export const PHONE_NUMBER_VALIDATOR = (rawValue: Maybe<string>) => {
  if (isNone(rawValue) || rawValue.length === 0) {
    return null;
  }

  const value = rawValue.replace(/\s+/g, '').trim();

  // Must start with + sign
  if (!value.startsWith('+')) {
    return 'validation_error.phone_number_format' as const;
  }

  // Must have at least the + sign and some digits
  if (value.length <= 1) {
    return 'validation_error.required' as const;
  }

  // Remove + sign for further processing
  const numberPart = value.slice(1);

  // Must contain only digits after the + sign
  if (!/^\d+$/.test(numberPart)) {
    return 'validation_error.phone_number_format' as const;
  }

  // Find matching format
  const knownFormat = VALID_PHONE_FORMATS.find((format) =>
    numberPart.startsWith(format.prefix),
  );

  if (!knownFormat) {
    return 'validation_error.phone_number_format' as const;
  }

  // Check if it's just the prefix without local number
  if (numberPart === knownFormat.prefix) {
    return 'validation_error.required' as const;
  }

  // Check total length (prefix + local number)
  if (
    numberPart.length !==
    knownFormat.prefix.length + knownFormat.numberCount
  ) {
    return 'validation_error.phone_number_length' as const;
  }

  return null;
};

export const validatePhoneNumberLength =
  (countryCode: string) => (value: string) => {
    const format = VALID_PHONE_FORMATS.find(
      (format) => format.prefix === countryCode,
    );

    value = value.replace(/\s+/g, '');

    if (format) {
      return value.length !== format.numberCount && value.length !== 0
        ? t('validation_error.phone_number_length', {
            length: format.numberCount,
          })
        : null;
    } else {
      return null;
    }
  };

export function usePhoneFields(init: FormFieldInit<string>) {
  const { countryCode: countryCodeValue, localNumber: localNumberValue } =
    useMemo(
      () =>
        parsePhoneNumber(
          init.value,
          VALID_PHONE_FORMATS.map((format) => format.prefix),
        ),
      [init.value],
    );

  const countryCode = useFormField({
    value: countryCodeValue,
    required: init.required,
    disabled: init.disabled,
    validators: [...(init.validators ?? [])],
  });

  const lengthValidator = useValidator(
    validatePhoneNumberLength(countryCode.value),
    [countryCode.value],
  );

  const localNumber = useFormField({
    value: localNumberValue,
    required: init.required,
    disabled: init.disabled,
    validators: [
      ...(init.validators ?? []),
      PHONE_NUMBER_FORMAT_VALIDATOR,
      lengthValidator,
    ],
  });

  return {
    countryCode,
    localNumber,
  };
}
